import { useCallback, useEffect } from 'react';
import { DateFilterType, DEFAULT_FILTER_VALUES, DEFAULT_SORT_VALUE } from '../constants/filters';
import { UserTicketViewOption } from '../data/types';
import { usePortalData } from '../data/usePortalData';
import { useListingsSortAndFiltersContext } from './useListingsSortAndFiltersContext';
export const useTicketListingsSortAndFilters = () => {
  const {
    data
  } = usePortalData();
  const {
    view,
    sort,
    offset,
    createDateFilter,
    lastActivityFilter,
    statusFilter,
    ticketView,
    searchString,
    conversationSearch,
    setFiltersState
  } = useListingsSortAndFiltersContext();
  const setView = useCallback(value => setFiltersState(state => Object.assign({}, state, {
    view: value
  })), [setFiltersState]);
  const setSort = useCallback(value => setFiltersState(state => Object.assign({}, state, {
    sort: value.length > 0 ? value : DEFAULT_SORT_VALUE,
    offset: 0
  })), [setFiltersState]);
  const setOffset = useCallback(value => setFiltersState(state => Object.assign({}, state, {
    offset: value
  })), [setFiltersState]);
  const setCreateDateFilter = useCallback(value => setFiltersState(state => Object.assign({}, state, {
    createDateFilter: value,
    offset: 0
  })), [setFiltersState]);
  const setLastActivityFilter = useCallback(value => setFiltersState(state => Object.assign({}, state, {
    lastActivityFilter: value,
    offset: 0
  })), [setFiltersState]);
  const setStatusFilter = useCallback(value => setFiltersState(state => Object.assign({}, state, {
    statusFilter: value,
    offset: 0
  })), [setFiltersState]);
  const setTicketView = useCallback(value => setFiltersState(state => Object.assign({}, state, {
    ticketView: value,
    offset: 0
  })), [setFiltersState]);
  const setSearchString = useCallback(value => setFiltersState(state => Object.assign({}, state, {
    searchString: value,
    offset: 0
  })), [setFiltersState]);
  const setConversationSearch = useCallback(value => setFiltersState(state => Object.assign({}, state, {
    conversationSearch: value
  })), [setFiltersState]);

  // Enforce constraints based on portal configuration
  useEffect(() => {
    if (!data) return;
    const {
      configuredTicketListProperties,
      userTicketViewOptions
    } = data;
    const validSortProperties = new Set(['createdate',
    //createdate is always a valid sortable property
    ...configuredTicketListProperties.map(p => p.propertyName)]);
    setFiltersState(state => {
      let next = state;
      if (!configuredTicketListProperties.find(({
        propertyName
      }) => propertyName === 'createdate') && state.createDateFilter !== DateFilterType.ALL_TIME) {
        next = Object.assign({}, next, {
          createDateFilter: DateFilterType.ALL_TIME
        });
      }
      if (!configuredTicketListProperties.find(({
        propertyName
      }) => propertyName === 'hs_lastactivitydate') && state.lastActivityFilter !== DateFilterType.ALL_TIME) {
        next = Object.assign({}, next, {
          lastActivityFilter: DateFilterType.ALL_TIME
        });
      }
      if (!userTicketViewOptions.includes(UserTicketViewOption.COMPANY) && state.ticketView !== UserTicketViewOption.PERSONAL) {
        next = Object.assign({}, next, {
          ticketView: UserTicketViewOption.PERSONAL
        });
      }
      const validSort = state.sort.filter(token => {
        const prop = token.startsWith('-') ? token.slice(1) : token;
        return validSortProperties.has(prop);
      });
      if (!validSort.length) {
        next = Object.assign({}, next, {
          sort: DEFAULT_SORT_VALUE
        });
      } else if (validSort.length !== state.sort.length) {
        next = Object.assign({}, next, {
          sort: validSort
        });
      }
      if (next !== state) {
        next = Object.assign({}, next, {
          offset: 0
        });
      }
      return next;
    });
  }, [data, setFiltersState]);
  const resetFilters = useCallback(() => {
    setFiltersState(state => Object.assign({}, state, {
      statusFilter: DEFAULT_FILTER_VALUES.statusFilter,
      createDateFilter: DEFAULT_FILTER_VALUES.createDateFilter,
      lastActivityFilter: DEFAULT_FILTER_VALUES.lastActivityFilter,
      conversationSearch: '',
      offset: 0
    }));
  }, [setFiltersState]);
  const resetSort = useCallback(() => {
    setSort(DEFAULT_SORT_VALUE);
  }, [setSort]);
  const activeFilterCount = (statusFilter !== DEFAULT_FILTER_VALUES.statusFilter ? 1 : 0) + (createDateFilter !== DEFAULT_FILTER_VALUES.createDateFilter ? 1 : 0) + (lastActivityFilter !== DEFAULT_FILTER_VALUES.lastActivityFilter ? 1 : 0) + (conversationSearch !== '' ? 1 : 0);
  const hasActiveFilters = activeFilterCount > 0 || searchString !== '';
  return {
    view,
    sort,
    offset,
    createDateFilter,
    lastActivityFilter,
    statusFilter,
    ticketView,
    searchString,
    conversationSearch,
    setView,
    setSort,
    setOffset,
    setCreateDateFilter,
    setLastActivityFilter,
    setStatusFilter,
    setTicketView,
    setSearchString,
    setConversationSearch,
    resetFilters,
    resetSort,
    activeFilterCount,
    hasActiveFilters
  };
};